import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ListingCard from '../components/ListingCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getListings, getCategories } from '../lib/api';

const ListingsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [listings, setListings] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
    const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || '');
    const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
    const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
    const [location, setLocation] = useState(searchParams.get('location') || '');
    const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await getCategories();
                setCategories(response.data);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchListings = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (selectedCategory) params.append('category', selectedCategory);
                if (selectedSubcategory) params.append('subcategory', selectedSubcategory);
                if (searchQuery) params.append('search', searchQuery);
                if (minPrice) params.append('min_price', minPrice);
                if (maxPrice) params.append('max_price', maxPrice);
                if (location) params.append('location', location);
                if (sortBy) params.append('sort_by', sortBy);

                const response = await getListings(Object.fromEntries(params));
                setListings(response.data.listings || response.data);
                setTotalCount(response.data.total || response.data.length);
            } catch (error) {
                console.error('Error fetching listings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchListings();
    }, [selectedCategory, selectedSubcategory, searchQuery, minPrice, maxPrice, location, sortBy]);

    const handleSearch = (e) => {
        e.preventDefault();
        updateUrlParams();
    };

    const updateUrlParams = () => {
        const newParams = new URLSearchParams();
        if (searchQuery) newParams.set('search', searchQuery);
        if (selectedCategory) newParams.set('category', selectedCategory);
        if (selectedSubcategory) newParams.set('subcategory', selectedSubcategory);
        if (minPrice) newParams.set('min_price', minPrice);
        if (maxPrice) newParams.set('max_price', maxPrice);
        if (location) newParams.set('location', location);
        if (sortBy && sortBy !== 'newest') newParams.set('sort', sortBy);
        setSearchParams(newParams);
    };

    const handleCategoryChange = (value) => {
        setSelectedCategory(value === 'all' ? '' : value);
        setSelectedSubcategory('');
    };

    const handleSubcategoryChange = (value) => {
        setSelectedSubcategory(value === 'all' ? '' : value);
    };

    const handleSortChange = (value) => {
        setSortBy(value);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedCategory('');
        setSelectedSubcategory('');
        setMinPrice('');
        setMaxPrice('');
        setLocation('');
        setSortBy('newest');
        setSearchParams({});
    };

    const currentCategory = categories.find(c => c.slug === selectedCategory);
    const hasFilters = searchQuery || selectedCategory || selectedSubcategory || minPrice || maxPrice || location;
    const hasAdvancedFilters = minPrice || maxPrice || location;

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen" data-testid="listings-page">
                {/* Header */}
                <div className="bg-navy text-white py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Browse Listings</h1>
                        <p className="text-gray-300">
                            Find hotel furniture, jobs, supplies, real estate and more
                        </p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Search & Filter Bar */}
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                        <form onSubmit={handleSearch} className="flex flex-col gap-4">
                            {/* Main Search Row */}
                            <div className="flex flex-col lg:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <Input
                                        type="text"
                                        placeholder="Search listings..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10"
                                        data-testid="search-input"
                                    />
                                </div>
                                
                                <div className="flex flex-wrap gap-3">
                                    <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
                                        <SelectTrigger className="w-[180px]" data-testid="category-select">
                                            <SelectValue placeholder="All Categories" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Categories</SelectItem>
                                            {categories.map((cat) => (
                                                <SelectItem key={cat.slug} value={cat.slug}>
                                                    {cat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    {currentCategory?.subcategories?.length > 0 && (
                                        <Select value={selectedSubcategory || 'all'} onValueChange={handleSubcategoryChange}>
                                            <SelectTrigger className="w-[160px]" data-testid="subcategory-select">
                                                <SelectValue placeholder="Subcategory" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Subcategories</SelectItem>
                                                {currentCategory.subcategories.map((sub) => (
                                                    <SelectItem key={sub.slug} value={sub.slug}>
                                                        {sub.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                        className={hasAdvancedFilters ? 'border-gold text-gold' : ''}
                                    >
                                        <SlidersHorizontal className="w-4 h-4 mr-2" />
                                        Filters
                                        {hasAdvancedFilters && <span className="ml-1 bg-gold text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">!</span>}
                                    </Button>

                                    <Button type="submit" className="bg-gold hover:bg-amber-600" data-testid="search-btn">
                                        Search
                                    </Button>
                                </div>
                            </div>

                            {/* Advanced Filters */}
                            {showAdvancedFilters && (
                                <div className="pt-4 border-t grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-600 mb-1 block">Min Price ($)</label>
                                        <Input
                                            type="number"
                                            placeholder="0"
                                            value={minPrice}
                                            onChange={(e) => setMinPrice(e.target.value)}
                                            min="0"
                                            data-testid="min-price-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-600 mb-1 block">Max Price ($)</label>
                                        <Input
                                            type="number"
                                            placeholder="Any"
                                            value={maxPrice}
                                            onChange={(e) => setMaxPrice(e.target.value)}
                                            min="0"
                                            data-testid="max-price-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-600 mb-1 block">Location</label>
                                        <Input
                                            type="text"
                                            placeholder="City, State..."
                                            value={location}
                                            onChange={(e) => setLocation(e.target.value)}
                                            data-testid="location-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-600 mb-1 block">Sort By</label>
                                        <Select value={sortBy} onValueChange={handleSortChange}>
                                            <SelectTrigger data-testid="sort-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="newest">Newest First</SelectItem>
                                                <SelectItem value="oldest">Oldest First</SelectItem>
                                                <SelectItem value="price_low">Price: Low to High</SelectItem>
                                                <SelectItem value="price_high">Price: High to Low</SelectItem>
                                                <SelectItem value="views">Most Viewed</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </form>

                        {/* Active Filters */}
                        {hasFilters && (
                            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
                                <span className="text-sm text-gray-500">Active filters:</span>
                                {searchQuery && (
                                    <span className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                        Search: {searchQuery}
                                        <button onClick={() => setSearchQuery('')}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                {selectedCategory && (
                                    <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                        {currentCategory?.name}
                                        <button onClick={() => handleCategoryChange('all')}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                {selectedSubcategory && (
                                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                        {currentCategory?.subcategories?.find(s => s.slug === selectedSubcategory)?.name}
                                        <button onClick={() => handleSubcategoryChange('all')}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                {minPrice && (
                                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                        Min: ${minPrice}
                                        <button onClick={() => setMinPrice('')}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                {maxPrice && (
                                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                        Max: ${maxPrice}
                                        <button onClick={() => setMaxPrice('')}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                {location && (
                                    <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                        Location: {location}
                                        <button onClick={() => setLocation('')}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600">
                                    Clear all
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Results Count & Sort */}
                    <div className="flex justify-between items-center mb-6">
                        <p className="text-gray-600">
                            <span className="font-semibold">{totalCount || listings.length}</span> listings found
                        </p>
                        {!showAdvancedFilters && (
                            <Select value={sortBy} onValueChange={handleSortChange}>
                                <SelectTrigger className="w-[180px]">
                                    <ArrowUpDown className="w-4 h-4 mr-2" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="newest">Newest First</SelectItem>
                                    <SelectItem value="oldest">Oldest First</SelectItem>
                                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                                    <SelectItem value="views">Most Viewed</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {/* Listings Grid */}
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="bg-white rounded-lg h-80 animate-pulse" />
                            ))}
                        </div>
                    ) : listings.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {listings.map((listing) => (
                                <ListingCard key={listing.id} listing={listing} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-white rounded-lg">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No listings found</h3>
                            <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
                            {hasFilters && (
                                <Button variant="outline" onClick={clearFilters}>
                                    Clear all filters
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default ListingsPage;
